// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { shopifyFetch } from '../lib/shopify';

const AuthContext = createContext();

// Per-operation error copy — Shopify reuses the same CustomerErrorCode enum
// (e.g. TAKEN, BLANK, INVALID) across unrelated mutations, so the right copy
// for one operation (e.g. "account already exists" on register) is wrong for
// another (e.g. an address-uniqueness error on updateAddress). Keep each
// operation's codes scoped to what that operation can actually return.
const AUTH_ERROR_MESSAGES = {
    login: {
        UNIDENTIFIED_CUSTOMER: 'No account found with that email and password.',
        INVALID_MULTIPASS_REQUEST: 'Invalid login request. Please try again.',
        TOO_MANY_ATTEMPTS_THROTTLED: 'Too many login attempts. Please wait a few minutes and try again.',
        INVALID: 'Invalid email or password.',
    },
    register: {
        TAKEN: 'An account with this email already exists.',
        INVALID_EMAIL: 'Please enter a valid email address.',
        PASSWORD_TOO_SHORT: 'Password must be at least 5 characters.',
        BLANK: 'Please fill in all required fields.',
        INVALID: 'Please enter a valid email address.',
    },
    updateDetails: {
        TAKEN: 'That phone number is already associated with another account.',
        INVALID_EMAIL: 'Please enter a valid email address.',
        BLANK: 'Please fill in all required fields.',
        INVALID: 'Please check the details you entered and try again.',
    },
    updateAddress: {
        TAKEN: 'This address is already saved to your account.',
        BLANK: 'Please fill in all required address fields.',
        INVALID: 'Please check the address details and try again.',
    },
    sendPasswordReset: {
        UNIDENTIFIED_CUSTOMER: 'No account found with that email address.',
        BLANK: 'Please enter your email address.',
    },
};

// Codes whose meaning doesn't depend on which mutation returned them
const COMMON_ERROR_MESSAGES = {
    CUSTOMER_DISABLED: 'This account has been disabled. Please contact support.',
    TOKEN_INVALID: 'Your session has expired. Please sign in again.',
};

function mapError(context, code, fallbackMessage) {
    return AUTH_ERROR_MESSAGES[context]?.[code]
        || COMMON_ERROR_MESSAGES[code]
        || fallbackMessage
        || 'Something went wrong. Please try again.';
}

const CUSTOMER_LOGIN = `
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_REGISTER = `
  mutation customerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        id
        email
        firstName
        lastName
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_INFO = `
  query getCustomer($customerAccessToken: String!) {
    customer(customerAccessToken: $customerAccessToken) {
      id
      firstName
      lastName
      email
      phone
      defaultAddress {
        id
        address1
        address2
        city
        province
        zip
        country
      }
      orders(first: 10) {
        edges {
          node {
            id
            orderNumber
            processedAt
            financialStatus
            fulfillmentStatus
            currentTotalPrice {
              amount
              currencyCode
            }
            subtotalPrice {
              amount
              currencyCode
            }
            totalShippingPrice {
              amount
              currencyCode
            }
            totalTax {
              amount
              currencyCode
            }
            shippingAddress {
              firstName
              lastName
              address1
              address2
              city
              province
              zip
              country
            }
            successfulFulfillments {
              trackingCompany
              trackingInfo {
                number
                url
              }
            }
            lineItems(first: 10) {
              edges {
                node {
                  title
                  quantity
                  variant {
                    title
                    price {
                      amount
                      currencyCode
                    }
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const CUSTOMER_UPDATE = `
  mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
    customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
      customer {
        id
        firstName
        lastName
        email
        phone
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_DEFAULT_ADDRESS_UPDATE = `
  mutation customerDefaultAddressUpdate($customerAccessToken: String!, $addressId: ID!) {
    customerDefaultAddressUpdate(customerAccessToken: $customerAccessToken, addressId: $addressId) {
      customer {
        id
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_ADDRESS_CREATE = `
  mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
    customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
      customerAddress {
        id
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_ADDRESS_UPDATE = `
  mutation customerAddressUpdate($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
    customerAddressUpdate(customerAccessToken: $customerAccessToken, id: $id, address: $address) {
      customerAddress {
        id
      }
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_PASSWORD_RESET = `
  mutation customerRecover($email: String!) {
    customerRecover(email: $email) {
      customerUserErrors {
        code
        message
      }
    }
  }
`;

const CUSTOMER_LOGOUT = `
  mutation customerAccessTokenDelete($customerAccessToken: String!) {
    customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
      deletedAccessToken
      userErrors {
        message
      }
    }
  }
`;

export function AuthProvider({ children }) {
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authLoading, setAuthLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('shopify_customer_token');
        if (token) {
            fetchCustomer(token);
        } else {
            setLoading(false);
        }
    }, []);

    async function fetchCustomer(token) {
        try {
            const data = await shopifyFetch(CUSTOMER_INFO, { customerAccessToken: token });
            if (data.customer) {
                setCustomer({ ...data.customer, accessToken: token });
            } else {
                // Shopify returned no customer for this token — it's invalid/expired
                localStorage.removeItem('shopify_customer_token');
            }
        } catch (err) {
            // Network/API failure, not a token rejection — leave the token in
            // place so the user isn't logged out over a flaky connection.
        } finally {
            setLoading(false);
        }
    }

    async function login(email, password) {
        setAuthLoading(true);
        try {
            const data = await shopifyFetch(CUSTOMER_LOGIN, { input: { email, password } });
            const { customerAccessToken, customerUserErrors } = data.customerAccessTokenCreate;
            if (customerUserErrors.length > 0) {
                const err = customerUserErrors[0];
                return { error: mapError('login', err.code, err.message) };
            }
            if (!customerAccessToken) {
                return { error: 'No account found with that email and password.' };
            }
            const token = customerAccessToken.accessToken;
            localStorage.setItem('shopify_customer_token', token);
            await fetchCustomer(token);
            return { success: true };
        } catch (err) {
            return { error: 'Something went wrong. Please try again.' };
        } finally {
            setAuthLoading(false);
        }
    }

    async function register(firstName, lastName, email, password) {
        setAuthLoading(true);
        try {
            const data = await shopifyFetch(CUSTOMER_REGISTER, { input: { firstName, lastName, email, password } });
            const { customerUserErrors } = data.customerCreate;
            if (customerUserErrors.length > 0) {
                const err = customerUserErrors[0];
                return { error: mapError('register', err.code, err.message) };
            }
            return await login(email, password);
        } catch (err) {
            return { error: 'Something went wrong. Please try again.' };
        } finally {
            setAuthLoading(false);
        }
    }

    async function updateDetails(fields) {
        const token = localStorage.getItem('shopify_customer_token');
        if (!token) return { error: 'You are not signed in.' };
        setAuthLoading(true);
        try {
            const data = await shopifyFetch(CUSTOMER_UPDATE, {
                customerAccessToken: token,
                customer: fields,
            });
            const { customerUserErrors } = data.customerUpdate;
            if (customerUserErrors.length > 0) {
                const err = customerUserErrors[0];
                return { error: mapError('updateDetails', err.code, err.message) };
            }
            await fetchCustomer(token);
            return { success: true };
        } catch (err) {
            return { error: 'Something went wrong. Please try again.' };
        } finally {
            setAuthLoading(false);
        }
    }

    async function updateAddress(address) {
        const token = localStorage.getItem('shopify_customer_token');
        if (!token) return { error: 'You are not signed in.' };
        setAuthLoading(true);
        try {
            const existingAddressId = customer?.defaultAddress?.id;

            if (existingAddressId) {
                // Customer already has a default address — update its fields in
                // place rather than creating a new, separate address record.
                const data = await shopifyFetch(CUSTOMER_ADDRESS_UPDATE, {
                    customerAccessToken: token,
                    id: existingAddressId,
                    address,
                });
                const { customerUserErrors } = data.customerAddressUpdate;
                await fetchCustomer(token);
                if (customerUserErrors.length > 0) {
                    const err = customerUserErrors[0];
                    return { error: mapError('updateAddress', err.code, err.message) };
                }
                return { success: true };
            }

            const data = await shopifyFetch(CUSTOMER_ADDRESS_CREATE, {
                customerAccessToken: token,
                address,
            });
            const { customerAddress, customerUserErrors } = data.customerAddressCreate;
            if (customerUserErrors.length > 0) {
                const err = customerUserErrors[0];
                return { error: mapError('updateAddress', err.code, err.message) };
            }
            const defaultData = await shopifyFetch(CUSTOMER_DEFAULT_ADDRESS_UPDATE, {
                customerAccessToken: token,
                addressId: customerAddress.id,
            });
            const { customerUserErrors: defaultErrors } = defaultData.customerDefaultAddressUpdate;
            await fetchCustomer(token);
            if (defaultErrors.length > 0) {
                const err = defaultErrors[0];
                return { error: mapError('updateAddress', err.code, err.message) };
            }
            return { success: true };
        } catch (err) {
            return { error: 'Something went wrong. Please try again.' };
        } finally {
            setAuthLoading(false);
        }
    }

    async function sendPasswordReset(email) {
        const targetEmail = email || customer?.email;
        if (!targetEmail) return { error: 'Please enter your email address.' };
        try {
            const data = await shopifyFetch(CUSTOMER_PASSWORD_RESET, { email: targetEmail });
            const { customerUserErrors } = data.customerRecover;
            if (customerUserErrors.length > 0) {
                const err = customerUserErrors[0];
                return { error: mapError('sendPasswordReset', err.code, err.message) };
            }
            return { success: true };
        } catch (err) {
            return { error: 'Something went wrong. Please try again.' };
        }
    }

    async function logout() {
        const token = localStorage.getItem('shopify_customer_token');
        if (token) {
            try {
                await shopifyFetch(CUSTOMER_LOGOUT, { customerAccessToken: token });
            } catch (err) {}
        }
        localStorage.removeItem('shopify_customer_token');
        setCustomer(null);
    }

    return (
        <AuthContext.Provider value={{
            customer,
            loading,
            authLoading,
            login,
            register,
            logout,
            updateDetails,
            updateAddress,
            sendPasswordReset,
            isLoggedIn: !!customer,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
}